import React, { FC } from 'react'
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer'
import CarePlanGrid from '../../../../types/CarePlanGrid'
import { formatUTCDate } from '../../../../utils/stringTools'

const styles = StyleSheet.create({
  headerTitle: {
    fontFamily: 'Helvetica-Bold'
  },
  headerText: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold'
  },
  headerView: {
    lineHeight: 'normal',
  },
  line: {
    marginTop: 10,
    marginBottom: 15,
    borderBottomColor: 'black',
    borderBottomWidth: 1
  },
  page: {
    padding: 30,
    position: 'relative'
  },
  carePlanTitle: {
    fontFamily: 'Helvetica-Bold',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  carePlanRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 5,
    fontSize: 11
  },
  carePlanProblemRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 15,
    fontSize: 9
  },
  carePlanName: {
    width: '65%'
  },
  carePlanText: {
    width: '35%'
  },
  pageContainer: {
    position: 'absolute',
    bottom: 15,
    right: 15,
    fontSize: 9,
  },
  carePlanDetails: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 5,
    marginTop: 10
  },
  carePlanContainer: {
    backgroundColor: 'rgb(245, 245, 245)',
    paddingTop: 10,
    paddingBottom: 5,
    paddingLeft: 10,
    paddingRight: 10,
    marginBottom: 10
  },
  problemName: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 12,
    marginBottom: 5
  },
  outcomeName: {
    fontSize: 11,
    marginBottom: 5,
    marginLeft: 10,
    marginTop: 5
  },
  actionName: {
    marginTop: 10,
    fontSize: 11,
    marginBottom: 5,
    marginLeft: 20
  },
  actionDetails: {
    fontSize: 11,
    marginBottom: 5,
    marginLeft: 20
  }
})

interface Props {
  carePlan: CarePlanGrid;
}

const Template: FC<Props> = ({ carePlan }: Props) => {
  const renderHeader = () => (
    <View style={{ textAlign: 'center' }}>
      <Text style={styles.headerTitle}>
        Care Plan Summary
      </Text>
      <View style={styles.line} />
    </View>
  )

  return (
    <Document title='Care Plan Summary'>
      <Page size='LETTER' style={styles.page} wrap>
        <View
          render={renderHeader}
          fixed
          style={styles.headerView}
        />
        <View key={carePlan.carePlanProviderSignatureId}>
          <View style={styles.carePlanRow}>
            <Text style={styles.carePlanName}>{`Name: ${carePlan.memberFullName || ''}`}</Text>
            <Text style={styles.carePlanText}>{`DOB: ${carePlan.memberDateOfBirth ? formatUTCDate(carePlan.memberDateOfBirth) : ''}`}</Text>
          </View>
          <View style={styles.carePlanRow}>
            <Text style={styles.carePlanName}>{`Member ID: ${carePlan.memberId || ''}`}</Text>
            <Text style={styles.carePlanText}>{`Member Age: ${carePlan.memberAge || ''}`}</Text>
          </View>
          <View style={styles.carePlanRow}>
            <Text style={styles.carePlanName}>{`Care Manager: ${carePlan.careManagerFullName || ''}`}</Text>
            <Text style={styles.carePlanText}>{`Gender: ${carePlan.memberGender || ''}`}</Text>
          </View>
          <View style={styles.carePlanRow}>
            <Text style={styles.carePlanName}>{`RN Care Manager: ${carePlan.nurseFullName || ''}`}</Text>
          </View>
          <View style={styles.carePlanRow}>
            <Text style={styles.carePlanName}>{`Location: ${carePlan.providerName || ''}`}</Text>
          </View>
          <View style={styles.carePlanRow}>
            <Text style={styles.carePlanName}>{`Acknowledge Date: ${carePlan.acknowledgeDate ? formatUTCDate(carePlan.acknowledgeDate) : ''}`}</Text>
          </View>
          <View style={styles.carePlanRow}>
            <Text style={styles.carePlanName}>{`Signer Name: ${carePlan.signedFullName || ''}`}</Text>
          </View>
          <Text style={styles.carePlanDetails}>Care Plan Details</Text>
          { carePlan.problem.map((problem, problemIdx) => (
              <View style={styles.carePlanContainer} wrap={false} key={`${carePlan.carePlanProviderSignatureId}_${problemIdx}`}>
                <Text style={styles.problemName}>{problem.problemName}</Text>
                { problem.objective.map((outcome, objectiveIdx) => (
                  <View key={`${carePlan.carePlanProviderSignatureId}_${problemIdx}_${objectiveIdx}`}>
                    <Text style={styles.outcomeName}>{outcome.objectiveType}: {outcome.objectiveName}</Text>
                    { outcome.action.map((action, actionIdx) => (
                      <View key={`${carePlan.carePlanProviderSignatureId}_${problemIdx}_${actionIdx}`}>
                        <Text style={styles.actionName}>Action: {action.actionSubType}: {action.actionName}</Text> 
                        <Text style={styles.actionDetails}>
                          Priority: {action.priority} | Start Date: {( action.effectiveDate && formatUTCDate(action.effectiveDate) ) || ''} | Closed Date: {( action.closeDate && formatUTCDate(action.closeDate) ) || ''}
                        </Text>
                      </View>
                    ))}
                  </View>
                ))}
              </View>
          ))}
        </View>     
        <View style={styles.pageContainer} fixed>
          <Text
            render={({ pageNumber, totalPages }) => totalPages > 0 ? `Page ${pageNumber} of ${totalPages}` : null}
          />
        </View>
      </Page>
    </Document>
  )
}

export default Template